import {
  ActionItemFilingConfig,
  MeetingsConfig,
} from "../agent-config-options";

const DEFAULT_AGENDA_TEMPLATE_ROOT = "<meetingsRoot>/_agenda-templates";

/**
 * An `actionItemFiling` config renders a subsection only when it
 * overrides a bundle default — i.e. the split is explicitly disabled or
 * the consumer supplies extra classification cues. A config that just
 * re-affirms the defaults (enabled, no cues) renders nothing because the
 * baked-in agent prompt already documents that behaviour.
 */
function actionItemFilingHasOverride(
  filing: ActionItemFilingConfig | undefined,
): filing is ActionItemFilingConfig {
  if (!filing) {
    return false;
  }
  return (
    filing.enabled === false ||
    (filing.humanOwnedCues ?? []).length > 0 ||
    (filing.agentWorkableCues ?? []).length > 0
  );
}

/**
 * Render the markdown subsections appended to the
 * `meeting-processing-workflow` rule when `AgentConfigOptions.meetings`
 * is supplied. Returns an empty string when the supplied config has
 * nothing to render (no meeting types and no meeting areas) so callers
 * can unconditionally concatenate the result.
 *
 * Two subsections are rendered, each gated on its own input array:
 *
 * 1. **Recognized meeting types** — rendered when `meetingTypes` is
 *    non-empty. Lists every declared type with its scope, kind,
 *    optional cadence, default duration, and agenda template path.
 *    Also documents the resolved `agendaTemplateRoot`.
 * 2. **Area → doc-root mapping** — rendered when `meetingAreas` is
 *    non-empty. Lists every declared area with its `id`, label, and
 *    docs-root-relative destination folder.
 *
 * Bundles consume the rendered string by appending it to their own
 * rule content. A caller that has no meeting types and no meeting
 * areas receives an empty string and can safely concatenate.
 */
export function renderMeetingTypesSection(
  meetings: MeetingsConfig | undefined,
): string {
  if (!meetings) {
    return "";
  }

  const types = meetings.meetingTypes ?? [];
  const areas = meetings.meetingAreas ?? [];
  const filingOverride = actionItemFilingHasOverride(meetings.actionItemFiling)
    ? meetings.actionItemFiling
    : undefined;

  if (types.length === 0 && areas.length === 0 && !filingOverride) {
    return "";
  }

  const sections: Array<string> = [];

  if (types.length > 0) {
    sections.push(renderMeetingTypes(types, meetings.agendaTemplateRoot));
  }

  if (areas.length > 0) {
    sections.push(renderMeetingAreas(areas));
  }

  if (filingOverride) {
    sections.push(renderActionItemFiling(filingOverride));
  }

  return sections.join("\n\n");
}

function renderMeetingTypes(
  types: ReadonlyArray<NonNullable<MeetingsConfig["meetingTypes"]>[number]>,
  agendaTemplateRoot: string | undefined,
): string {
  const resolvedRoot = agendaTemplateRoot ?? DEFAULT_AGENDA_TEMPLATE_ROOT;

  const lines: Array<string> = [
    "## Recognized meeting types",
    "",
    "This project declares a meeting-type taxonomy through",
    "`AgentConfigOptions.meetings.meetingTypes`. When classifying a",
    "meeting transcript, pick the `id` from this list that best fits the",
    "meeting; do **not** invent new type identifiers. The `Kind` column",
    "maps each concrete `id` onto the generic meeting-kind taxonomy",
    "(`planning` / `review` / `brainstorm` / `standup` / `external` /",
    "`other`) documented in the **Meeting type handling** section —",
    "apply the type-specific rules listed there based on this kind.",
    "Agenda-template paths below are resolved relative to",
    `\`${resolvedRoot}\`.`,
    "",
    "| ID | Label | Kind | Scope | Cadence | Default duration | Agenda template |",
    "|----|-------|------|-------|---------|------------------|-----------------|",
  ];

  for (const type of types) {
    const kind = type.kind ? `\`${type.kind}\`` : "—";
    const cadence = type.cadence ? `\`${type.cadence}\`` : "—";
    const duration =
      type.defaultDurationMinutes !== undefined
        ? `${type.defaultDurationMinutes} min`
        : "—";
    const template = type.agendaTemplatePath
      ? `\`${type.agendaTemplatePath}\``
      : "—";
    lines.push(
      `| \`${type.id}\` | ${type.label} | ${kind} | \`${type.scope}\` | ${cadence} | ${duration} | ${template} |`,
    );
  }

  return lines.join("\n");
}

function renderMeetingAreas(
  areas: ReadonlyArray<NonNullable<MeetingsConfig["meetingAreas"]>[number]>,
): string {
  const lines: Array<string> = [
    "## Area → doc-root mapping",
    "",
    "This project declares a meeting-area routing map through",
    "`AgentConfigOptions.meetings.meetingAreas`. When a meeting note's",
    "frontmatter carries an `areas:` list, route phase-4 direct edits",
    "into the corresponding sub-trees of the docs root",
    "(`AgentPathsConfig.docsRoot`). See the **Areas filtering** section",
    "of the `meeting-analyst` agent for the full gating contract.",
    "Meetings whose `areas` list is empty or does not match any",
    "declared entry fall back to the default meetings root.",
    "",
    "| Area ID | Label | Doc-root sub-folder |",
    "|---------|-------|---------------------|",
  ];

  for (const area of areas) {
    lines.push(`| \`${area.id}\` | ${area.label} | \`${area.docRoot}\` |`);
  }

  return lines.join("\n");
}

function renderActionItemFiling(filing: ActionItemFilingConfig): string {
  const lines: Array<string> = [
    "## Action-item filing policy",
    "",
    "This project tunes the Phase 4 (Link) agent-workability split",
    "through `AgentConfigOptions.meetings.actionItemFiling`. See the",
    "**Action-item filing policy** section of the `meeting-analyst`",
    "agent for the full baked-in test.",
    "",
  ];

  if (filing.enabled === false) {
    lines.push(
      "- **Split disabled.** Phase 4 falls back to the legacy behaviour:",
      "  file a follow-up issue for every action item that is not",
      "  already covered by a requirement or ADR draft, including",
      "  human-owned tasks. The notes `## Action Items` table is still",
      "  written, but it is no longer the sole record for human",
      "  follow-ups.",
    );
  } else {
    lines.push(
      "- **Split enabled** (default). Agent-workable doc deliverables are",
      "  filed through their dedicated downstream channel; human-owned",
      "  tasks are recorded only in the notes `## Action Items` table.",
    );
  }

  const humanCues = filing.humanOwnedCues ?? [];
  if (humanCues.length > 0) {
    lines.push(
      "",
      "**Additional human-owned cues** (recorded notes-only, never",
      "filed), in addition to the bundle's built-in list:",
      "",
      ...humanCues.map((cue) => `- ${cue}`),
    );
  }

  const agentCues = filing.agentWorkableCues ?? [];
  if (agentCues.length > 0) {
    lines.push(
      "",
      "**Additional agent-workable cues** (filed through the matching",
      "downstream channel), in addition to the bundle's built-in list:",
      "",
      ...agentCues.map((cue) => `- ${cue}`),
    );
  }

  return lines.join("\n");
}
